import { toast } from "react-toastify";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";

import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import { Fragment, useState } from "react";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { orderList, orderOrdering } from "../../../../services/orderService";
import dayjs from "dayjs";
import ActionBar from "../../../../components/elements/ActionBar";
import { lngDetect } from "../../../../helpers/lngDetect";
import Badge from "../../../../components/elements/Badge";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import { eventList } from "../../../../services/eventService";
import { useOrganization } from "../../../../context/OrganizationContext";
import OrderEarningDrawer from "./OrderEarning/Drawer/OrderEarningDrawer";
import { useOutletContext } from "react-router-dom";
import { t } from "i18next";

const PromoterSales = () => {
  const { promoter } = useOutletContext();
  const [selectedItems, setSelectedItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [eventkeyword, seteventKeyword] = useState("");
  const eventsearch = useDebounce(eventkeyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [eventfilters, setEventFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState({
    isOpen: false,
    data: null,
  });

  // Query for sales data (similar to earnings but for sales)
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["promoter-sales", filters, search],
    queryFn: async () => {
      const res = await orderList({
        viewAs: "promoter",
        paymentStatus: "success",
        event: promoter?.data?.eventData?._id,
        salesOnly: true, // Add this flag to get only sales data
        ...filters,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: eventData } = useQuery({
    queryKey: [
      "Event",
      eventfilters,
      eventsearch,
      promoter?.data?.organizationData?._id,
    ],
    queryFn: async () => {
      const res = await eventList({
        organization: promoter?.data?.organizationData?._id,
        ...eventfilters,
        ...(eventsearch && { keyword: eventsearch }),
      });

      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!promoter?.data?.organizationData?._id,
  });

  const tableHeadData = [
    {
      _id: "OrderId",
      name: t("Order Id"),
      align: "left",
    },
    {
      _id: 1,
      name: t("Sale Amount"),
      align: "left",
    },
    {
      _id: 2,
      name: t("Event Name"),
      align: "left",
      ...tableFilter({
        filterKey: "event",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterSearch: seteventKeyword,
        isFilterSearch: true,
        filterList: eventData?.data?.map((item) => ({
          name: lngDetect(item?.title),
          value: item?._id,
          type: "checkbox",
        })),
      }),
    },
    {
      _id: 3,
      name: t("Customer"),
      align: "left",
    },
    {
      _id: 4,
      name: t("Sale Date"),
      align: "left",
    },
    {
      _id: 5,
      name: t("Details"),
      align: "left",
    },
  ];

  // Table Body Data for Sales
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.orderId || "-",
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.promoterInfo?.promoterFee ? (
            <span className="font-semibold">
              ${item?.promoterInfo?.promoterFee}
            </span>
          ) : (
            "-"
          ),
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: lngDetect(item?.eventData?.title) || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            (item?.userData?.firstName ?? "") +
            " " +
            (item?.userData?.lastName ?? ""),
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: dayjs(item?.createdAt).format("MMM DD, YYYY") || "-",
        },
        {
          _id: "action",
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: "details",
              name: t("Details"),
              icon: "fa-regular fa-circle-info",
              onClick: () => {
                setDetailsDrawerOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
          ],
        },
      ],
    };
  });

  const options = [
    {
      id: "userDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "Delete Sales",
    },
  ];

  function actionHandler(val) {
    switch (val) {
      case "userDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;
      default:
        break;
    }
  }

  const { mutate: handleOrdering, isPending } = useMutation({
    mutationFn: async ({ newData, oldData }) => {
      const res = await orderOrdering({
        ordering_data: newData?.map((item, i) => ({
          id: item?._id,
          ordering: i,
        })),
      });
      return { res, newData, oldData };
    },
    onSuccess: ({ res, newData, oldData }) => {
      if (res?.status !== 200) {
        queryClient.setQueryData(
          ["promoter-sales", filters, search],
          (state) => {
            if (state) {
              return { ...state, data: oldData };
            }
          }
        );
        toast.warning(res?.message || res?.error);
      }
    },
  });

  return (
    <div className="w-full relative p-6 bg-white">
      <div className="space-y-3 px-2">
        <div className="flex justify-between items-center border-b border-gray-300">
          <div className="flex flex-col gap-2 w-full">
            <div className="text-2xl md:text-4xl 2xl:text-6xl tracking-tighter font-bold text-black mb-5">
              {t("Sales")}
            </div>
          </div>
        </div>
        <Fragment>
          <div className="flex flex-col md:flex-row items-center justify-end gap-3">
            <div className="w-full md:w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={data?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            <div className="relative w-full md:w-auto">
              <Search
                placeholder={t("Search here")}
                divClasses={"!border-slate-300 !bg-slate-50"}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
            {selectedItems?.length > 0 && (
              <div className="flex items-center gap-2">
                <ActionBar options={options} onChange={actionHandler} />
                <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                  {selectedItems?.length} {t("Selected")}
                  <button
                    onClick={() => setSelectedItems([])}
                    type="button"
                    className="cursor-pointer"
                  >
                    <i className="fa-fw fa-light fa-xmark"></i>
                  </button>
                </span>
              </div>
            )}
          </div>
          <Table
            tableHeadData={tableHeadData}
            tableData={tableData}
            isLoder={isLoading}
            pageCount={data?.metadata?.totalPages}
            currentPage={filters?.page}
            onPageChange={(val) => {
              handleFilterChange("page", val, setFilters);
              setSelectedItems([]);
            }}
            originalData={[...(data?.data || [])]}
            setOriginalData={(val) => {
              queryClient.setQueryData(
                ["promoter-sales", filters, search],
                (state) => {
                  if (state) {
                    return { ...state, data: val };
                  }
                }
              );
            }}
            handleOrdering={(newData, oldData) =>
              handleOrdering({ newData, oldData })
            }
            isFetching={isFetching}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />
        </Fragment>
      </div>

      <OrderEarningDrawer
        open={detailsDrawerOpen?.isOpen}
        data={detailsDrawerOpen?.data}
        close={() => {
          setDetailsDrawerOpen({ isOpen: false, data: null });
        }}
      />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, data: null })}
        onConfirm={() => {
          setDeleteModal({ isOpen: false, data: null });
        }}
        title="Delete Sales"
        message="Are you sure you want to delete the selected sales records? This action cannot be undone."
      />
    </div>
  );
};

export default PromoterSales;
